import { Client, type IMessage, type StompSubscription } from '@stomp/stompjs';
import { getToken } from '@/store/modules/auth/shared';
import { getWsBrokerURL } from '@/utils/ws';

export type StompMessageHandler = (message: IMessage) => void;
export type StompUnsubscribe = () => void;
export type StompConnectionState = 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'disconnecting' | 'failed';
export type StompConnectFailureReason =
  | 'TOKEN_MISSING'
  | 'TIMEOUT'
  | 'WEBSOCKET_ERROR'
  | 'STOMP_ERROR'
  | 'DISCONNECTED';
export type StompConnectResult = { ok: true } | { ok: false; reason: StompConnectFailureReason; message?: string };

interface ConnectionDiagnostic {
  state: StompConnectionState;
  reason?: StompConnectFailureReason;
  message?: string;
  closeCode?: number;
  closeReason?: string;
  reconnectAttempts: number;
  generation: number;
  timestamp: number;
}

type ConnectionWaiter = (result: StompConnectResult) => void;

let client: Client | null = null;
let clientToken = '';
let disconnectPromise: Promise<void> | null = null;
let preparePromise: Promise<void> | null = null;
let idleDisconnectTimer = 0;
let generation = 0;
let reconnectAttempts = 0;

const handlersByDestination = new Map<string, Set<StompMessageHandler>>();
const stompSubscriptions = new Map<string, StompSubscription>();
const connectionWaiters = new Set<ConnectionWaiter>();

const MAX_RECONNECT_ATTEMPTS = 10;
const RECONNECT_DELAY = 3000;
const CONNECT_TIMEOUT = 15000;
/** 最后一个业务订阅取消后保留连接，便于弹窗短时间内再次执行。 */
const IDLE_DISCONNECT_DELAY = 60000;

let lastDiagnostic: ConnectionDiagnostic = {
  state: 'idle',
  reconnectAttempts: 0,
  generation: 0,
  timestamp: Date.now()
};

function updateDiagnostic(
  state: StompConnectionState,
  details: Partial<Omit<ConnectionDiagnostic, 'state' | 'reconnectAttempts' | 'generation' | 'timestamp'>> = {}
) {
  lastDiagnostic = {
    state,
    reconnectAttempts,
    generation,
    timestamp: Date.now(),
    ...details
  };
}

function resolveConnectionWaiters(result: StompConnectResult) {
  connectionWaiters.forEach(resolve => resolve(result));
  connectionWaiters.clear();
}

function clearIdleDisconnectTimer() {
  window.clearTimeout(idleDisconnectTimer);
  idleDisconnectTimer = 0;
}

function ensureStompSubscription(destination: string) {
  if (!client?.connected || stompSubscriptions.has(destination)) return;

  const stompSub = client.subscribe(destination, message => {
    handlersByDestination.get(destination)?.forEach(handler => handler(message));
  });
  stompSubscriptions.set(destination, stompSub);
}

function resubscribeAll() {
  stompSubscriptions.forEach(sub => sub.unsubscribe());
  stompSubscriptions.clear();
  handlersByDestination.forEach((_handlers, destination) => ensureStompSubscription(destination));
}

async function deactivateCurrent(clearHandlers: boolean) {
  const currentClient = client;
  generation += 1;
  client = null;
  clientToken = '';
  updateDiagnostic('disconnecting');

  stompSubscriptions.forEach(sub => sub.unsubscribe());
  stompSubscriptions.clear();
  if (clearHandlers) handlersByDestination.clear();
  if (currentClient) await currentClient.deactivate();

  updateDiagnostic('idle');
}

async function prepareClient() {
  clearIdleDisconnectTimer();
  const token = getToken();
  if (!token) {
    const result: StompConnectResult = { ok: false, reason: 'TOKEN_MISSING' };
    updateDiagnostic('failed', { reason: result.reason });
    resolveConnectionWaiters(result);
    return;
  }

  if (disconnectPromise) await disconnectPromise;

  if (client && clientToken !== token) {
    disconnectPromise = deactivateCurrent(false).finally(() => {
      disconnectPromise = null;
    });
    await disconnectPromise;
  }

  if (client?.active) {
    if (client.connected) {
      reconnectAttempts = 0;
      updateDiagnostic('connected');
      resubscribeAll();
      resolveConnectionWaiters({ ok: true });
    }
    return;
  }

  reconnectAttempts = 0;
  generation += 1;
  const currentGeneration = generation;
  updateDiagnostic('connecting');

  const stompClient = new Client({
    brokerURL: getWsBrokerURL(token),
    reconnectDelay: RECONNECT_DELAY,
    heartbeatIncoming: 10000,
    heartbeatOutgoing: 10000,
    onConnect: () => {
      if (client !== stompClient || generation !== currentGeneration) return;
      reconnectAttempts = 0;
      updateDiagnostic('connected');
      resubscribeAll();
      resolveConnectionWaiters({ ok: true });
    },
    onStompError: frame => {
      if (client !== stompClient || generation !== currentGeneration) return;
      const message = frame.headers.message || frame.body || 'STOMP broker error';
      updateDiagnostic('failed', { reason: 'STOMP_ERROR', message });
      resolveConnectionWaiters({ ok: false, reason: 'STOMP_ERROR', message });
    },
    onWebSocketError: () => {
      if (client !== stompClient || generation !== currentGeneration) return;
      updateDiagnostic('reconnecting', { reason: 'WEBSOCKET_ERROR' });
    },
    onWebSocketClose: event => {
      if (client !== stompClient || generation !== currentGeneration) return;

      reconnectAttempts += 1;
      if (reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
        updateDiagnostic('reconnecting', {
          reason: 'DISCONNECTED',
          closeCode: event.code,
          closeReason: event.reason
        });
        return;
      }

      const result: StompConnectResult = {
        ok: false,
        reason: 'DISCONNECTED',
        message: event.reason
      };
      updateDiagnostic('failed', {
        reason: result.reason,
        message: result.message,
        closeCode: event.code,
        closeReason: event.reason
      });
      resolveConnectionWaiters(result);
      disconnectPromise = deactivateCurrent(false).finally(() => {
        disconnectPromise = null;
      });
    }
  });

  client = stompClient;
  clientToken = token;
  stompClient.activate();
}

function ensureClientPrepared() {
  if (preparePromise) return preparePromise;

  preparePromise = prepareClient().finally(() => {
    preparePromise = null;
  });
  return preparePromise;
}

/** 主动开始建立连接。需要等待结果时请使用 waitForStompConnected。 */
export function connectStomp() {
  return ensureClientPrepared();
}

export function isStompConnected() {
  return Boolean(client?.connected && clientToken === getToken());
}

/** 确保 WebSocket 已连接；相同连接过程由所有调用方共同等待。 */
export async function waitForStompConnected(timeoutMs = CONNECT_TIMEOUT): Promise<StompConnectResult> {
  if (client?.connected && clientToken === getToken()) return { ok: true };

  return new Promise(resolve => {
    let settled = false;
    let timer = 0;
    const finish: ConnectionWaiter = result => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      connectionWaiters.delete(finish);
      resolve(result);
    };
    timer = window.setTimeout(() => {
      updateDiagnostic('failed', { reason: 'TIMEOUT' });
      finish({ ok: false, reason: 'TIMEOUT' });
    }, timeoutMs);

    connectionWaiters.add(finish);
    ensureClientPrepared().catch(error => {
      const message = error instanceof Error ? error.message : String(error);
      updateDiagnostic('failed', { reason: 'WEBSOCKET_ERROR', message });
      resolveConnectionWaiters({ ok: false, reason: 'WEBSOCKET_ERROR', message });
    });
  });
}

/** 退出登录或应用卸载时断开全局 WebSocket。 */
export function disconnectStomp() {
  if (disconnectPromise) return disconnectPromise;

  clearIdleDisconnectTimer();
  resolveConnectionWaiters({ ok: false, reason: 'DISCONNECTED' });
  disconnectPromise = deactivateCurrent(true).finally(() => {
    disconnectPromise = null;
  });
  return disconnectPromise;
}

/** 订阅 STOMP destination，连接建立前注册会在 onConnect 后自动订阅。 */
export function subscribeStomp(destination: string, handler: StompMessageHandler): StompUnsubscribe {
  clearIdleDisconnectTimer();
  if (!handlersByDestination.has(destination)) handlersByDestination.set(destination, new Set());
  handlersByDestination.get(destination)!.add(handler);
  ensureStompSubscription(destination);

  return () => {
    const handlers = handlersByDestination.get(destination);
    if (!handlers) return;

    handlers.delete(handler);
    if (handlers.size > 0) return;

    handlersByDestination.delete(destination);
    stompSubscriptions.get(destination)?.unsubscribe();
    stompSubscriptions.delete(destination);

    if (handlersByDestination.size === 0) {
      clearIdleDisconnectTimer();
      idleDisconnectTimer = window.setTimeout(() => {
        if (handlersByDestination.size === 0 && !disconnectPromise) {
          disconnectPromise = deactivateCurrent(false).finally(() => {
            disconnectPromise = null;
          });
        }
      }, IDLE_DISCONNECT_DELAY);
    }
  };
}

/** 用于问题上报与诊断，不包含完整 Token 或 WebSocket URL。 */
export function getStompConnectionDiagnostic(): Readonly<ConnectionDiagnostic> {
  return { ...lastDiagnostic };
}
